'use client';

import { useEffect, useState } from 'react';
import { collection, doc, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/firebase/config';
import type { CecopLive, CecopProcesion } from '@/lib/types';
import { ProcesionMap, ProcesionLegend } from '@/components/cecop/ProcesionMap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DoorOpen, MessageCircle, Youtube } from 'lucide-react';

/**
 * Tarjeta pública de procesión en directo. La habilita el admin desde el CECOP
 * (`publicCard: true`) y sustituye a los botones + emblema del hero.
 *
 * Lectura sin sesión: las reglas dejan leer solo los docs con `publicCard: true`,
 * por eso la consulta filtra por ese campo (si no, el `list` falla entero).
 */
export function usePublicProcesion() {
  const [procesion, setProcesion] = useState<CecopProcesion | null>(null);
  const [live, setLive] = useState<CecopLive | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'cecop_procesiones'), where('publicCard', '==', true), limit(1));
    const u = onSnapshot(
      q,
      (snap) => setProcesion(snap.empty ? null : ({ id: snap.docs[0].id, ...(snap.docs[0].data() as any) })),
      () => setProcesion(null),
    );
    return () => u();
  }, []);

  useEffect(() => {
    if (!procesion?.id) { setLive(null); return; }
    const u = onSnapshot(
      doc(db, 'cecop_live', procesion.id),
      (snap) => setLive(snap.exists() ? ({ id: snap.id, ...(snap.data() as any) }) : null),
      () => setLive(null),
    );
    return () => u();
  }, [procesion?.id]);

  return { procesion, live };
}

/** Saca el id del vídeo de cualquier forma de URL de YouTube (watch, youtu.be, live, embed). */
export function youtubeId(url?: string): string | null {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|live\/|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

const btnShape = {
  clipPath: 'polygon(0% 0%, calc(100% - 16px) 0%, 100% 16px, 100% 100%, 16px 100%, 0% calc(100% - 16px))',
};

export function ProcesionPublicCard({ procesion, live }: { procesion: CecopProcesion; live: CecopLive | null }) {
  const color = procesion.color || '#d4af37';
  const vid = youtubeId(procesion.youtubeUrl);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className="relative mx-auto w-full max-w-5xl overflow-hidden rounded-3xl border p-5 backdrop-blur-md md:p-7"
      style={{ borderColor: `${color}66`, background: 'rgba(8,8,10,.72)', boxShadow: `0 0 40px ${color}22, 0 20px 60px rgba(0,0,0,.6)` }}
    >
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl" style={{ background: `${color}33` }} />

      {/* Título + chip EN DIRECTO */}
      <div className="relative flex flex-col items-center gap-3">
        <motion.span
          animate={{ scale: [1, 1.06, 1], boxShadow: ['0 0 0 0 rgba(244,63,94,.5)', '0 0 0 12px rgba(244,63,94,0)', '0 0 0 0 rgba(244,63,94,0)'] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="inline-flex items-center gap-2 rounded-full border border-rose-500/50 bg-rose-500/25 px-4 py-1.5 text-xs font-black uppercase tracking-[0.25em] text-rose-200"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-rose-400" />
          En directo
        </motion.span>

        <h2 className="text-center font-headline text-3xl font-black uppercase leading-tight tracking-tight text-white drop-shadow-[0_2px_18px_rgba(0,0,0,.9)] md:text-5xl">
          {procesion.titulo}
        </h2>
        {procesion.descripcion && (
          <p className="max-w-2xl text-center text-sm text-white/65">{procesion.descripcion}</p>
        )}
      </div>

      {/* Directo de YouTube + GPS */}
      <div className="relative mt-6 grid gap-4 lg:grid-cols-[1fr_340px]">
        <div className="overflow-hidden rounded-2xl border border-white/12 bg-black">
          {vid ? (
            <div className="relative aspect-video">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${vid}?autoplay=1&mute=1&playsinline=1&rel=0`}
                title={procesion.titulo}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 h-full w-full"
              />
            </div>
          ) : (
            <div className="grid aspect-video place-items-center text-sm text-white/40">
              Sin directo de YouTube configurado
            </div>
          )}
        </div>

        {/* GPS con su propia cabecera "RECORRIDO" + indicador de directo */}
        <div
          className="flex flex-col items-center gap-2.5 rounded-2xl border p-3"
          style={{ borderColor: `${color}44`, background: 'rgba(0,0,0,.35)' }}
        >
          <div className="flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-2 font-headline text-sm font-black uppercase tracking-[0.28em] text-white">
              <span style={{ color }}>✦</span>
              Recorrido
              <span style={{ color }}>✦</span>
            </span>
            <span className="flex items-center gap-1.5 text-[0.6rem] font-black uppercase tracking-[0.18em] text-rose-300">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-400" />
              En directo
            </span>
          </div>

          <ProcesionMap
            map={procesion.map}
            viewport={procesion.viewport}
            color={color}
            iglesia={procesion.iglesia}
            estacion={procesion.estacion}
            ida={procesion.ida}
            vuelta={procesion.vuelta}
            live={live?.entries || []}
            compact
            fixedHeight={260}
            showLabels
          />

          <ProcesionLegend color={color} />
        </div>
      </div>

      {/* Acciones */}
      <div className="relative mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row">
        <Link
          to="/apply"
          style={btnShape}
          className="group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden bg-gradient-to-r from-primary to-amber-500 px-7 text-sm font-bold text-primary-foreground shadow-[0_0_22px_rgba(250,204,21,0.4)] transition-transform hover:scale-[1.04]"
        >
          <DoorOpen className="h-4 w-4" /> Entrar al servidor
        </Link>

        <a
          href={procesion.youtubeUrl || 'https://www.youtube.com'}
          target="_blank"
          rel="noopener noreferrer"
          style={btnShape}
          className="group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden bg-gradient-to-r from-red-600 to-red-500 px-7 text-sm font-bold text-white shadow-[0_0_22px_rgba(239,68,68,0.35)] transition-transform hover:scale-[1.04]"
        >
          <Youtube className="h-4 w-4" /> Ver en directo
        </a>

        <a
          href="https://discord.gg/rmJGq5YbTE"
          target="_blank"
          rel="noopener noreferrer"
          style={btnShape}
          className="group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden border border-white/25 bg-white/5 px-7 text-sm font-bold text-white backdrop-blur-md transition-all hover:scale-[1.04] hover:bg-white/10"
        >
          <MessageCircle className="h-4 w-4" /> Únete al Discord
        </a>
      </div>
    </motion.div>
  );
}
